import { useTranslation } from 'react-i18next';
import { useLiveQuery } from 'dexie-react-hooks';
import { MEASUREMENT_KEYS, MEASUREMENT_LIMITS, measurementTrend } from '@kraftwerk/shared';
import { db } from '../../db/local.js';
import { listMeasurementDays } from '../../db/journal.js';
import { formatDay } from '../../format.js';

/**
 * Körpermaße über die Zeit (23.10.2026): je gemessenem Maß der letzte Wert
 * und die Änderung — mit dem Datum des Vergleichs, damit eine Woche nicht
 * nach einem Monat aussieht. Nie gemessene Maße stehen nicht da; ohne
 * jede Messung fehlt die Kachel, die Eingabe steht im Tag.
 */
export function MeasurementTrendTile({ today }: { today: string }): React.JSX.Element | null {
  const { t, i18n } = useTranslation();
  const days = useLiveQuery(() => listMeasurementDays(db), [], null);
  if (!days || days.length === 0) return null;

  const number = (value: number) =>
    value.toLocaleString(i18n.language, { maximumFractionDigits: 1 });
  const rows = MEASUREMENT_KEYS.map((key) => ({
    key,
    trend: measurementTrend(days, key, today),
  })).filter((row) => row.trend.latest !== null);
  if (rows.length === 0) return null;

  return (
    <section aria-labelledby="measurements-heading" className="kw-tile flex flex-col gap-2">
      <h3 id="measurements-heading" className="kw-kicker">
        {t('journal.measurements.trend')}
      </h3>
      <dl className="flex flex-col gap-2 text-sm">
        {rows.map(({ key, trend }) => {
          const unit = MEASUREMENT_LIMITS[key].unit;
          const value = (amount: number) =>
            t(`journal.measurements.value.${unit}`, { value: number(amount) });
          const change = trend.change;
          return (
            <div key={key} className="flex flex-wrap items-baseline justify-between gap-x-3">
              <dt className="text-ink-muted">{t(`journal.measurements.${key}`)}</dt>
              <dd className="text-right">
                <span className="font-semibold">
                  {t('journal.measurements.latest', {
                    value: value(trend.latest!.value),
                    date: formatDay(trend.latest!.date, 'date', i18n.language),
                  })}
                </span>
                <span className="block text-xs text-ink-muted">
                  {change !== null && trend.previous
                    ? t('journal.measurements.change', {
                        change: `${change > 0 ? '+' : change < 0 ? '−' : '±'}${value(Math.abs(change))}`,
                        date: formatDay(trend.previous.date, 'date', i18n.language),
                      })
                    : t('journal.measurements.single')}
                </span>
              </dd>
            </div>
          );
        })}
      </dl>
    </section>
  );
}
